// Generated by OrchestKit Claude Plugin
// Created: 2026-10-04

import { familleDe, libelleDe, statutVente, type Famille } from '../../theme/statuts.ts';
import type { IdentiteBoutique } from './exporter-facture-pdf-types.ts';

/** RGB des familles de statut (`theme/tokens.css`, thème clair : un PDF n'a pas de mode sombre). */
const COULEUR_FAMILLE: Record<Famille, [number, number, number]> = {
  teal: [14, 138, 95],
  gold: [184, 110, 0],
  rust: [226, 100, 15],
  brick: [212, 42, 58],
  gray: [100, 116, 139],
};

const NOIR = [22, 32, 43] as const;
const GRIS = [91, 103, 118] as const;
const TRAIT = [221, 227, 234] as const;

/**
 * Bandeau d'identité, badge de statut, et les deux boîtes « Facturé à » /
 * infos de vente. Renvoie le Y où continuer (table des lignes).
 *
 * L'IFU (boutique ou client) ne s'affiche QUE s'il est fourni : une facture
 * sans IFU connu ne doit jamais montrer une ligne vide ou un placeholder —
 * même principe que RG-143 (ne jamais afficher ce qu'on ne sait pas).
 */
export function dessinerEnteteFacture(
  doc: import('jspdf').jsPDF,
  {
    marge,
    largeur,
    numero,
    boutique,
    dateHeure,
    statut,
    motifContrepassation,
    vendeur,
    client,
    ifuClient,
    canal,
    total,
    resteDu,
  }: {
    marge: number;
    largeur: number;
    numero: string;
    boutique: IdentiteBoutique;
    dateHeure: string;
    statut: 'Validée' | 'Contrepassée';
    motifContrepassation?: string | null | undefined;
    vendeur: string;
    client: string;
    ifuClient?: string | null | undefined;
    canal: string;
    total: number;
    resteDu: number;
  },
): number {
  doc.setDrawColor(...TRAIT);
  doc.setLineWidth(0.3);
  doc.rect(marge, 10, 22, 22); // repère du logo — aucun logo chargé aujourd'hui

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...NOIR);
  doc.text(boutique.nom, marge + 26, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GRIS);
  let yIdentite = 21;
  if (boutique.adresse !== null && boutique.adresse !== undefined && boutique.adresse !== '') {
    doc.text(boutique.adresse, marge + 26, yIdentite);
    yIdentite += 4;
  }
  const contact = [
    boutique.telephone !== null && boutique.telephone !== undefined && boutique.telephone !== ''
      ? `Tél. ${boutique.telephone}`
      : null,
    boutique.email !== null && boutique.email !== undefined && boutique.email !== '' ? boutique.email : null,
  ].filter((valeur): valeur is string => valeur !== null);
  if (contact.length > 0) {
    doc.text(contact.join(' · '), marge + 26, yIdentite);
    yIdentite += 4;
  }
  const identifiants = [
    boutique.ifu !== null && boutique.ifu !== undefined && boutique.ifu !== '' ? `IFU ${boutique.ifu}` : null,
    boutique.rccm !== null && boutique.rccm !== undefined && boutique.rccm !== '' ? `RCCM ${boutique.rccm}` : null,
  ].filter((valeur): valeur is string => valeur !== null);
  if (identifiants.length > 0) {
    doc.text(identifiants.join(' · '), marge + 26, yIdentite);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(0, 113, 189);
  doc.text('FACTURE', largeur - marge, 16, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...NOIR);
  doc.text(numero, largeur - marge, 22, { align: 'right' });

  const statutCalcule = statut === 'Contrepassée' ? 'contrepassee' : statutVente(total, resteDu);
  const couleurBadge = COULEUR_FAMILLE[familleDe(statutCalcule)];
  const libelleBadge = libelleDe(statutCalcule).toUpperCase();
  const largeurBadge = doc.getTextWidth(libelleBadge) + 8;
  doc.setDrawColor(...couleurBadge);
  doc.setLineWidth(0.4);
  doc.roundedRect(largeur - marge - largeurBadge, 25, largeurBadge, 7, 3.5, 3.5, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...couleurBadge);
  doc.text(libelleBadge, largeur - marge - largeurBadge / 2, 29.5, { align: 'center' });

  doc.setDrawColor(0, 113, 189);
  doc.setLineWidth(0.8);
  doc.line(marge, 38, largeur - marge, 38);

  // ── Facturé à / Infos ────────────────────────────────────────────────────
  const largeurBoiteGauche = (largeur - marge * 2 - 8) * 0.55;
  const xBoiteDroite = marge + largeurBoiteGauche + 8;
  const largeurBoiteDroite = largeur - marge - xBoiteDroite;
  const yBoites = 44;
  const hauteurBoites = 26;

  doc.setDrawColor(...TRAIT);
  doc.roundedRect(marge, yBoites, largeurBoiteGauche, hauteurBoites, 2, 2, 'S');
  doc.roundedRect(xBoiteDroite, yBoites, largeurBoiteDroite, hauteurBoites, 2, 2, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GRIS);
  doc.text('FACTURÉ À', marge + 5, yBoites + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...NOIR);
  doc.text(client, marge + 5, yBoites + 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GRIS);
  if (ifuClient !== null && ifuClient !== undefined && ifuClient !== '') {
    doc.text(`IFU ${ifuClient}`, marge + 5, yBoites + 19);
  }

  const champInfo = (libelle: string, valeur: string, y: number): void => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...GRIS);
    doc.text(libelle, xBoiteDroite + 5, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NOIR);
    doc.text(valeur, xBoiteDroite + largeurBoiteDroite - 5, y, { align: 'right' });
  };
  champInfo('Date', dateHeure, yBoites + 7);
  champInfo('Boutique', boutique.nom, yBoites + 12.5);
  champInfo('Vendeur', vendeur, yBoites + 18);
  champInfo('Canal', canal, yBoites + 23.5);

  let curseur = yBoites + hauteurBoites + 8;

  if (statut === 'Contrepassée') {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(212, 42, 58);
    const texte =
      motifContrepassation === null || motifContrepassation === undefined || motifContrepassation === ''
        ? 'Vente contrepassée.'
        : `Vente contrepassée — ${motifContrepassation}`;
    doc.text(texte, marge, curseur);
    curseur += 7;
  }

  return curseur;
}
